// @ts-strict-ignore
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import {
  selectActiveNodeEditorTab,
  selectDiffActionByPublicId,
  selectFocusedAction,
  selectFocusedActionForDiff,
  selectInitialFocusedAction,
  selectInitialOutcomeActions,
  selectOutcomeActions,
  selectPublicVersion,
} from 'flow/store/redux/selectors';
import { getActionPrecedenceIndex, sortActionsByPrecedenceIndex } from 'flow/service/actions';
import { useInitialWorkflowStore } from './useInitialWorkflowStore';
import {
  StyledDiffViewContainer,
  StyledDiffViewPane,
  StyledBadgeContainer,
  StyledDiffViewGrid,
  StyledDiffViewColumn,
} from './styles';
import { Badge } from '@usealloy/component-library';
import { StyledCodeStyleText } from 'flow/editor/styles';
import DiffContextProvider, { panes } from './DiffContext';
import { Operation } from 'flow/service/changelog/types';
import type { Action } from 'flow/store/engine/types';
import { DiffCard } from './DiffCard';
import { useAppSelector } from '~/store/reduxHooks';

type DiffSplitViewProps = {
  children: ReactNode;
  showBadges?: boolean;
};

export { getDiffStatusById } from './DiffContext';

/**
 * Renders `children` twice, side by side: "Existing" against a store/form scoped to the
 * initial snapshot, and "Modified" against the ambient store/form. Each pane is wrapped in
 * its own DiffContextProvider so children can diff against each other.
 */
export function DiffSplitView({ children, showBadges = true }: DiffSplitViewProps) {
  const activeNodeEditorTab = useAppSelector(selectActiveNodeEditorTab);
  const currentFocusedAction = useAppSelector(selectFocusedAction);
  const initialFocusedAction = useAppSelector(selectInitialFocusedAction);
  const workflowVersion = useAppSelector(selectPublicVersion);
  // A removed action only exists in the initial snapshot, so the current one can't key the lookup
  const focusedActionForDiff = useAppSelector(selectFocusedActionForDiff);
  const actionDiffOperation = useAppSelector(
    state => selectDiffActionByPublicId(focusedActionForDiff?.publicId ?? '')(state),
  );
  const initialStore = useInitialWorkflowStore();

  // Field-level highlighting only makes sense for a MODIFIED action
  const highlightFields = actionDiffOperation === Operation.Modify;

  const leftBadges = {
    [Operation.Modify]: <Badge status="neutral">Existing</Badge>,
    [Operation.Remove]: <Badge status="danger">Removed</Badge>,
    [Operation.Add]: (
      <StyledCodeStyleText>This node was added in v{workflowVersion + 1}.</StyledCodeStyleText>
    ),
  };

  const rightBadges = {
    [Operation.Modify]: <Badge status="attention">Modified</Badge>,
    [Operation.Add]: <Badge status="success">Added</Badge>,
    [Operation.Remove]: (
      <StyledCodeStyleText>This node was removed in v{workflowVersion + 1}.</StyledCodeStyleText>
    ),
  };

  return (
    <StyledDiffViewContainer>
      <StyledDiffViewPane>
        <DiffContextProvider
          highlightFields={highlightFields}
          pane={panes.left}
        >
          {showBadges && (
          <StyledBadgeContainer>
            {actionDiffOperation ? leftBadges[actionDiffOperation] : null}
          </StyledBadgeContainer>
          )}
          <DiffCard
            action={initialFocusedAction}
            pane={panes.left}
            store={initialStore}
            formTab={activeNodeEditorTab}
            child={children}
          />
        </DiffContextProvider>
      </StyledDiffViewPane>
      <StyledDiffViewPane>
        <DiffContextProvider
          highlightFields={highlightFields}
          pane={panes.right}
        >
          {showBadges && (
            <StyledBadgeContainer>
              {actionDiffOperation ? rightBadges[actionDiffOperation] : null}
            </StyledBadgeContainer>
          )}
          <DiffCard
            action={currentFocusedAction}
            pane={panes.right}
            formTab={activeNodeEditorTab}
            child={children}
          />
        </DiffContextProvider>
      </StyledDiffViewPane>
    </StyledDiffViewContainer>
  );
}

type DiffViewActionsSplitProps = {
  children: (action: Action) => ReactNode;
  showBadges?: boolean;
};

function sortOutcomesForDiffColumn(outcomeActions: Action[]): Action[] {
  const hasPrecedence = (action: Action) => (
    typeof getActionPrecedenceIndex(action) === 'number'
  );

  return [
    ...sortActionsByPrecedenceIndex(outcomeActions.filter(hasPrecedence), 'DESC'),
    ...outcomeActions.filter(action => !hasPrecedence(action)),
  ];
}

/**
 * Diffs every outcome action side by side: one
 * badge row per pane, with one column per snapshot
 */
export function DiffOutcomeActionsSplitView({
  children,
  showBadges = true,
}: DiffViewActionsSplitProps) {
  const initialOutcomeActions = useAppSelector(selectInitialOutcomeActions);
  const currentOutcomeActions = useAppSelector(selectOutcomeActions);
  const initialStore = useInitialWorkflowStore();

  const initialColumn = useMemo(
    () => sortOutcomesForDiffColumn(initialOutcomeActions),
    [initialOutcomeActions],
  );
  const currentColumn = useMemo(
    () => sortOutcomesForDiffColumn(currentOutcomeActions),
    [currentOutcomeActions],
  );

  return (
    <StyledDiffViewGrid>
      <StyledDiffViewColumn pane={panes.left}>
        {showBadges && (
          <StyledBadgeContainer>
            <Badge status="neutral">Existing</Badge>
          </StyledBadgeContainer>
        )}
        <DiffContextProvider pane={panes.left}>
          {initialColumn.map(action => (
            <DiffCard
              key={action.publicId}
              action={action}
              child={children(action)}
              pane={panes.left}
              store={initialStore}
            />
          ))}
        </DiffContextProvider>
      </StyledDiffViewColumn>
      <StyledDiffViewColumn pane={panes.right}>
        {showBadges && (
          <StyledBadgeContainer>
            <Badge status="attention">Modified</Badge>
          </StyledBadgeContainer>
        )}
        <DiffContextProvider pane={panes.right}>
          {currentColumn.map(action => (
            <DiffCard
              key={action.publicId}
              action={action}
              child={children(action)}
              pane={panes.right}
            />
          ))}
        </DiffContextProvider>
      </StyledDiffViewColumn>
    </StyledDiffViewGrid>
  );
}
